import { bind } from 'bind-decorator';
import { AnnotatedVideoAnnotation } from 'labxchange-client';
import * as React from 'react';
import Skeleton from 'react-loading-skeleton';
import * as UI from 'ui/components';
import { WrappedMessage } from 'utils';
import { HeadingTwo } from 'elements';

import messages from './displayMessages';
import { RichText } from 'elements/components/RichText';
import { SanitizeConfigOptions } from 'elements/utils/sanitization';

interface UserAnswer {
    correct?: boolean;
    value?: string;
}

export interface Props {
    annotation: AnnotatedVideoAnnotation | AnnotatedVideoAnnotation[];
    onCloseQuestion(): void;
}

interface State {
    userAnswers: { [key: string]: UserAnswer};
    showResults: boolean;
    hasErrors: boolean;
    totalQuestionsRemaining: number;
}

export class AnnotationQuestionSidebar extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            userAnswers: {},
            showResults: false,
            hasErrors: false,
            totalQuestionsRemaining: Array.isArray(this.props.annotation) ? this.props.annotation.length : 1,
        };
    }

    public render () {
        if (Array.isArray(this.props.annotation)) {
            return  <>
                    <div className='annotated-video-annotation-question-sidebar'>
                        <div className='annotated-video-annotation-question-sidebar-content'>
                            <div className='question-label'>
                                <HeadingTwo
                                    level={2}
                                    titleDescriptor={messages.coupledQuestionsTitle}
                                />
                            </div>
                            {this.props.annotation.map((annotation, index) => <AnnotationQuestion annotation={annotation} onCloseQuestion={this.closeQuestion} isCoupled={true}/>)}
                            <div className='annotated-video-annotation-question-close-button'>
                                <UI.Button
                                    btnStyle='outline'
                                    icon='x'
                                    onClick={this.props.onCloseQuestion}
                                    label={messages.endQuestionButtonLabel}
                                />
                            </div>
                        </div>
                    </div></>;
        } else {
            return  <>
                    <div className='annotated-video-annotation-question-sidebar'>
                        <div className='annotated-video-annotation-question-sidebar-content'>
                        {<AnnotationQuestion annotation={this.props.annotation} onCloseQuestion={this.props.onCloseQuestion} isCoupled={false}/>}
                        </div>
                    </div></>;
        }

    }

    @bind private closeQuestion() {
        let totalQuestionsRemaining = this.state.totalQuestionsRemaining;
        totalQuestionsRemaining -= 1;
        if (totalQuestionsRemaining === 0) {
            window.setTimeout(() => this.props.onCloseQuestion(), 1000);
        } else {
            this.setState({totalQuestionsRemaining});
        }
    }
}

interface AnnotationQuestionProps {
    annotation: AnnotatedVideoAnnotation;
    isCoupled: boolean;
    onCloseQuestion(): void;
}

interface AnnotationQuestionState {
    userAnswers: { [key: string]: UserAnswer};
    showResults: boolean;
    hasErrors: boolean;
    isAnsweredCorrectly: boolean;
    showFeedback: Set<string>;
    isMultiCorrect: boolean;
}

export class AnnotationQuestion extends React.PureComponent<AnnotationQuestionProps, AnnotationQuestionState> {
    constructor(props: AnnotationQuestionProps) {
        super(props);
        this.state = {
            userAnswers: {},
            showResults: false,
            hasErrors: false,
            isAnsweredCorrectly: false,
            isMultiCorrect: this.getIsMultiCorrectQuestion(),
            showFeedback: new Set(),
        };
    }
    public render () {
        const showResults = this.state.showResults;
        this.setState({
            isMultiCorrect: this.getIsMultiCorrectQuestion()
        });
        return <>
            {this.props.isCoupled ? null : <div className='annotated-video-annotation-question-sidebar-title'>
                {this.props.annotation.title}
            </div>}
            <div className='annotated-video-annotation-question'>
                {!this.props.isCoupled ? null : <div className='inner-question-label'>{messages.coupledQuestionsInnerQuestionLabel.defaultMessage}</div>}
                <div className='annotated-video-annotation-question-title'>
                    <RichText
                        innerHtml={this.props.annotation.question!.question}
                        sanitizeConfig={SanitizeConfigOptions.UnsafeLightHTML}
                    />
                </div>
                <ol className='annotated-video-annotation-question-answers'>
                {this.props.annotation.question!.answers.map((answer, index) => {
                    const userAnswer = this.getUserAnswer(answer.id);
                    return <><li
                        key={index}
                        aria-hidden={true}
                        onClick={() => this.onClickAnswer(
                            answer.id,
                            !userAnswer.correct,
                            userAnswer.value || '')}
                        className={`answer ${userAnswer.correct ? 'answer-active ': ' '} ${this.state.isMultiCorrect ? 'multicorrect ': ' '}
                        ${this.state.showFeedback.has(answer.id) ? answer.correct ? 'correct' : 'incorrect' : ''}`}>
                        <button
                            className='unstyled'
                            aria-label={answer.text}
                            onClick={() => this.onClickAnswer(
                                answer.id,
                                !userAnswer.correct,
                                userAnswer.value || '')}>{answer.text}</button>
                    </li>
                    {this.state.showFeedback.has(answer.id) && answer.feedback !== ''? <li className={`feedback-${answer.correct ? 'correct' : 'incorrect'}`}>{answer.feedback}</li>: null}
                    </>;
                })}
                </ol>
                {showResults && this.renderResults()}
                <div className='annotated-video-annotation-question-close-button'>
                    <UI.Button
                        btnStyle='primary'
                        onClick={this.onSubmit}
                        label={messages.submitQuestionButtonLabel}
                        disabled={this.state.isAnsweredCorrectly}
                    />
                </div>
            </div>
            {this.props.isCoupled ? null : <div className='annotated-video-annotation-question-close-button'>
                <UI.Button
                    btnStyle='outline'
                    icon='x'
                    onClick={this.props.onCloseQuestion}
                    label={messages.endQuestionButtonLabel}
                />
            </div>}
        </>;
    }

    @bind private getUserAnswer(id: string): UserAnswer {
        const userAnswer = this.state.userAnswers[id];
        if (userAnswer) { return userAnswer; }
        return {correct: false, value: ''};
    }

    @bind private onClickAnswer(id: string, correct: boolean, value: string): void {
        let userAnswers = {...this.state.userAnswers};
        if(this.state.isMultiCorrect){
            userAnswers[id] = {correct, value};
        } else {
            userAnswers = {};
            userAnswers[id] = {correct, value};
        }
        this.setState({userAnswers});
    }

    @bind private renderResults() {
        if (this.state.hasErrors) {
            if(this.state.isMultiCorrect) {
                return <><div className='answer-results-incorrect'>
                    <WrappedMessage message={messages.multiCorrectQuestionIncorrectResults}/>
                    </div></>;
            } else {
                return null;
            }
        }
        return <div className='answer-results-correct'>
            <WrappedMessage message={messages.questionCorrectResults}/>
        </div>;
    }

    @bind private hasErrors() {
        return this.props.annotation.question!.answers.some((answer) => {
            const userAnswer = this.getUserAnswer(answer.id);
            return userAnswer.correct !== answer.correct;
        });
    }

    @bind private onSubmit() {
        this.checkFeedback();
        const hasErrors = this.hasErrors();
        if (!hasErrors) {
            this.setState({isAnsweredCorrectly : true});
            this.props.onCloseQuestion();
        }
        this.setState({hasErrors, showResults: true});
    }

    private checkFeedback() {
        const showFeedback = new Set() as Set<string>;
        this.props.annotation.question!.answers.forEach((answer) => {
            const userAnswer = this.getUserAnswer(answer.id);
            if (userAnswer.correct) {
                showFeedback.add(answer.id);
            }
        });
        this.setState({showFeedback});
    }

    private getIsMultiCorrectQuestion() {
        if (this.props.annotation.question?.answers) {
            return !(this.props.annotation.question?.answers.filter((x) => x.correct === true).length === 1);
        }
        return false;
    }
}

export const AnnotationProblemSidebarSkeleton = () => <div className='annotated-video-question-sidebar'>
    <Skeleton/>
    <Skeleton/>
    <Skeleton/>
</div>;
